import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  X, ChevronRight, ChevronLeft, Check, User, Shield, Users, Eye,
  Plus, Lock, Mail, Phone, AlertCircle, CheckCircle, Loader
} from 'lucide-react'
import clsx from 'clsx'
import { usersAPI, groupsAPI, rolesAPI } from '../services/api'

// ── Role catalogue ───────────────────────────────────────────────────────────

const ROLE_GROUPS = [
  {
    label: 'Incident Management',
    color: 'blue',
    roles: [
      {
        name: 'IncidentViewer',
        label: 'View Incidents',
        description: 'Can view incidents and their full details'
      },
      {
        name: 'IncidentCreator',
        label: 'Create Incidents',
        description: 'Can create new incidents and view them'
      },
      {
        name: 'IncidentRequesterEditor',
        label: 'Change Requester',
        description: 'Can change who raised (reported) an incident'
      },
      {
        name: 'IncidentImplementorEditor',
        label: 'Change Implementor',
        description: 'Can reassign the person working on an incident'
      },
      {
        name: 'IncidentApproverEditor',
        label: 'Change Approvers',
        description: 'Can update the approver list on an incident'
      }
    ]
  },
  {
    label: 'Support Levels',
    color: 'violet',
    roles: [
      { name: 'SupportL1', label: 'Support L1', description: 'First-line support — triage and initial response' },
      { name: 'SupportL2', label: 'Support L2', description: 'Second-line support — technical investigation' },
      { name: 'SupportL3', label: 'Support L3', description: 'Third-line / escalation support' }
    ]
  },
  {
    label: 'Platform Administration',
    color: 'red',
    roles: [
      { name: 'Admin', label: 'Administrator', description: 'Full platform access — manage users, groups and settings' }
    ]
  }
]

const COLOR = {
  blue: {
    border: 'border-blue-400',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    checkBorder: 'border-blue-500',
    checkBg: 'bg-blue-500'
  },
  violet: {
    border: 'border-violet-400',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    checkBorder: 'border-violet-500',
    checkBg: 'bg-violet-500'
  },
  red: {
    border: 'border-red-400',
    bg: 'bg-red-50',
    text: 'text-red-700',
    checkBorder: 'border-red-500',
    checkBg: 'bg-red-500'
  }
}

const STEPS = [
  { id: 1, label: 'User Details', icon: User },
  { id: 2, label: 'Access Roles', icon: Shield },
  { id: 3, label: 'Group', icon: Users },
  { id: 4, label: 'Review', icon: Eye }
]

// ── Component ────────────────────────────────────────────────────────────────

export default function UserOnboardingWizard({ isOpen, onClose, onSuccess }) {
  const queryClient = useQueryClient()

  const [step, setStep] = useState(1)
  const [errors, setErrors] = useState({})

  const [details, setDetails] = useState({
    name: '', email: '', phone: '', password: '', confirmPassword: '',
    type: 'Client', portal_access: 'Client Portal', status: 'Active'
  })

  const [selectedRoles, setSelectedRoles] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [groupSearch, setGroupSearch] = useState('')

  // Load groups
  const { data: groupsRes } = useQuery({
    queryKey: ['groups-lookup'],
    queryFn: () => groupsAPI.list({ limit: 100 }),
    enabled: isOpen
  })
  const allGroups = groupsRes?.data?.data || []
  const filteredGroups = allGroups.filter(
    (g) =>
      String(g.status || '').toLowerCase() === 'active' &&
      (groupSearch === '' ||
        g.name.toLowerCase().includes(groupSearch.toLowerCase()) ||
        (g.description || '').toLowerCase().includes(groupSearch.toLowerCase()))
  )

  const createUser = useMutation({
    mutationFn: (payload) => usersAPI.create(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      onSuccess?.(res?.data?.data)
      handleClose()
    }
  })

  const handleClose = () => {
    setStep(1)
    setErrors({})
    setDetails({ name: '', email: '', phone: '', password: '', confirmPassword: '', type: 'Client', portal_access: 'Client Portal', status: 'Active' })
    setSelectedRoles([])
    setSelectedGroups([])
    setGroupSearch('')
    onClose()
  }

  const validateStep1 = () => {
    const e = {}
    if (!details.name.trim()) e.name = 'Full name is required'
    if (!details.email.trim()) e.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.email)) e.email = 'Invalid email address'
    if (!details.password) e.password = 'Password is required'
    else if (details.password.length < 8) e.password = 'Minimum 8 characters'
    if (details.password !== details.confirmPassword) e.confirmPassword = 'Passwords do not match'
    setErrors(e)
    return Object.keys(e).length === 0
  }

  const next = () => {
    if (step === 1 && !validateStep1()) return
    setErrors({})
    setStep((s) => Math.min(4, s + 1))
  }

  const back = () => setStep((s) => Math.max(1, s - 1))

  const toggleRole = (roleName) => {
    setSelectedRoles((prev) =>
      prev.includes(roleName) ? prev.filter((r) => r !== roleName) : [...prev, roleName]
    )
  }

  const toggleGroup = (gid) => {
    setSelectedGroups((prev) =>
      prev.includes(gid) ? prev.filter((id) => id !== gid) : [...prev, gid]
    )
  }

  const handleSubmit = () => {
    createUser.mutate({
      name: details.name.trim(),
      email: details.email.trim().toLowerCase(),
      phone: details.phone.trim() || null,
      password: details.password,
      type: details.type,
      portal_access: details.portal_access,
      status: details.status,
      role_names: selectedRoles,
      group_ids: selectedGroups
    })
  }

  if (!isOpen) return null

  const selectedGroupObjects = allGroups.filter((g) => selectedGroups.includes(g.id))
  const roleLabel = (name) => ROLE_GROUPS.flatMap((g) => g.roles).find((r) => r.name === name)?.label || name

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Onboard New User</h2>
            <p className="text-xs text-gray-500 mt-0.5">AIOps24 – AI-POWERED SUPPORT & OPS Platform</p>
          </div>
          <button onClick={handleClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 pt-4 pb-2 shrink-0">
          <div className="flex items-center gap-0">
            {STEPS.map((s, idx) => {
              const Icon = s.icon
              const done = step > s.id
              const active = step === s.id
              return (
                <div key={s.id} className="flex items-center flex-1">
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className={clsx(
                      'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all',
                      done ? 'bg-green-500 text-white' :
                      active ? 'bg-blue-600 text-white ring-4 ring-blue-100' :
                      'bg-gray-100 text-gray-400'
                    )}>
                      {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={clsx('text-[10px] font-medium whitespace-nowrap', active ? 'text-blue-600' : done ? 'text-green-600' : 'text-gray-400')}>
                      {s.label}
                    </span>
                  </div>
                  {idx < STEPS.length - 1 && (
                    <div className={clsx('flex-1 h-0.5 mx-2 mt-[-12px]', done ? 'bg-green-400' : 'bg-gray-200')} />
                  )}
                </div>
              )
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* ── Step 1: User Details ── */}
          {step === 1 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Enter the new user's basic information.</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                  <input
                    type="text" value={details.name} placeholder="e.g. John Smith"
                    onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
                    className={clsx('w-full border rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500', errors.name ? 'border-red-400' : 'border-gray-300')}
                  />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="email" value={details.email} placeholder="john@company.com"
                      onChange={(e) => setDetails((d) => ({ ...d, email: e.target.value }))}
                      className={clsx('w-full border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500', errors.email ? 'border-red-400' : 'border-gray-300')}
                    />
                  </div>
                  {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="tel" value={details.phone} placeholder="+91 9876543210"
                      onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password" value={details.password} placeholder="Min. 8 characters"
                      onChange={(e) => setDetails((d) => ({ ...d, password: e.target.value }))}
                      className={clsx('w-full border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500', errors.password ? 'border-red-400' : 'border-gray-300')}
                    />
                  </div>
                  {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Confirm Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="password" value={details.confirmPassword} placeholder="Re-enter password"
                      onChange={(e) => setDetails((d) => ({ ...d, confirmPassword: e.target.value }))}
                      className={clsx('w-full border rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500', errors.confirmPassword ? 'border-red-400' : 'border-gray-300')}
                    />
                  </div>
                  {errors.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors.confirmPassword}</p>}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">User Type</label>
                  <select value={details.type} onChange={(e) => setDetails((d) => ({ ...d, type: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Client">Client</option>
                    <option value="Vendor">Vendor</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Portal Access</label>
                  <select value={details.portal_access} onChange={(e) => setDetails((d) => ({ ...d, portal_access: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Client Portal">Client Portal</option>
                    <option value="Vendor Portal">Vendor Portal</option>
                    <option value="Both">Both Portals</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Initial Status</label>
                  <select value={details.status} onChange={(e) => setDetails((d) => ({ ...d, status: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Active">Active</option>
                    <option value="Pending Approval">Pending Approval</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Access Roles ── */}
          {step === 2 && (
            <div className="space-y-5">
              <p className="text-sm text-gray-500">Select the access privileges for this user. You can combine roles from multiple groups.</p>
              {ROLE_GROUPS.map((group) => (
                <div key={group.label}>
                  <h3 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">{group.label}</h3>
                  <div className="space-y-2">
                    {group.roles.map((role) => {
                      const checked = selectedRoles.includes(role.name)
                      return (
                        <button
                          key={role.name}
                          type="button"
                          onClick={() => toggleRole(role.name)}
                          className={clsx(
                            'w-full flex items-start gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all',
                            checked
                              ? `${COLOR[group.color].border} ${COLOR[group.color].bg}`
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          )}
                        >
                          <div className={clsx(
                            'w-5 h-5 rounded flex items-center justify-center border-2 shrink-0 mt-0.5 transition-all',
                            checked ? `${COLOR[group.color].checkBorder} ${COLOR[group.color].checkBg}` : 'border-gray-300'
                          )}>
                            {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </div>
                          <div>
                            <p className={clsx('text-sm font-semibold', checked ? COLOR[group.color].text : 'text-gray-800')}>{role.label}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{role.description}</p>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
              {selectedRoles.length === 0 && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5">
                  <AlertCircle className="w-3.5 h-3.5" /> No roles selected — the user will have read-only access.
                </p>
              )}
            </div>
          )}

          {/* ── Step 3: Group Assignment ── */}
          {step === 3 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Assign this user to one or more groups. Groups control team-level access and ticket routing.</p>
              <input
                type="text" placeholder="Search groups…" value={groupSearch}
                onChange={(e) => setGroupSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
              {filteredGroups.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-6">No groups found.</p>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {filteredGroups.map((g) => {
                    const checked = selectedGroups.includes(g.id)
                    return (
                      <button
                        key={g.id} type="button" onClick={() => toggleGroup(g.id)}
                        className={clsx(
                          'w-full flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-all',
                          checked ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                        )}
                      >
                        <div className={clsx(
                          'w-5 h-5 rounded flex items-center justify-center border-2 shrink-0 transition-all',
                          checked ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                        )}>
                          {checked && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={clsx('text-sm font-semibold truncate', checked ? 'text-blue-700' : 'text-gray-800')}>{g.name}</p>
                          {g.description && <p className="text-xs text-gray-500 truncate">{g.description}</p>}
                        </div>
                        {g.business_unit && (
                          <span className="text-[10px] text-gray-400 font-medium shrink-0">{g.business_unit}</span>
                        )}
                      </button>
                    )
                  })}
                </div>
              )}
              {selectedGroups.length === 0 && (
                <p className="text-xs text-gray-400">Group assignment is optional and can be updated later.</p>
              )}
            </div>
          )}

          {/* ── Step 4: Review ── */}
          {step === 4 && (
            <div className="space-y-5">
              <p className="text-sm text-gray-500">Review the configuration before creating the user.</p>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">User Details</span>
                </div>
                <div className="px-4 py-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <div><span className="text-gray-500">Name</span><p className="font-semibold text-gray-900">{details.name}</p></div>
                  <div><span className="text-gray-500">Email</span><p className="font-semibold text-gray-900">{details.email}</p></div>
                  <div><span className="text-gray-500">Type</span><p className="font-semibold text-gray-900">{details.type}</p></div>
                  <div><span className="text-gray-500">Portal</span><p className="font-semibold text-gray-900">{details.portal_access}</p></div>
                  <div><span className="text-gray-500">Status</span><p className="font-semibold text-gray-900">{details.status}</p></div>
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Access Roles ({selectedRoles.length})</span>
                </div>
                <div className="px-4 py-3">
                  {selectedRoles.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No roles assigned — read-only access</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((r) => (
                        <span key={r} className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                          <Shield className="w-3 h-3" />{roleLabel(r)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-4 py-2.5 border-b border-gray-200">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Groups ({selectedGroups.length})</span>
                </div>
                <div className="px-4 py-3">
                  {selectedGroupObjects.length === 0 ? (
                    <p className="text-sm text-gray-400 italic">No groups assigned</p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {selectedGroupObjects.map((g) => (
                        <span key={g.id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-700 text-xs font-semibold px-2.5 py-1 rounded-full">
                          <Users className="w-3 h-3" />{g.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {createUser.isError && (
                <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{createUser.error?.response?.data?.message || createUser.error?.message || 'Failed to create user'}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-2xl shrink-0">
          <button
            onClick={step === 1 ? handleClose : back}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-200 transition-colors"
          >
            {step === 1 ? <><X className="w-4 h-4" />Cancel</> : <><ChevronLeft className="w-4 h-4" />Back</>}
          </button>

          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Step {step} of {STEPS.length}</span>
            {step < 4 ? (
              <button
                onClick={next}
                className="flex items-center gap-1.5 px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
              >
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={createUser.isPending}
                className="flex items-center gap-1.5 px-5 py-2 bg-green-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 transition-colors disabled:opacity-60"
              >
                {createUser.isPending
                  ? <><Loader className="w-4 h-4 animate-spin" />Creating…</>
                  : <><CheckCircle className="w-4 h-4" />Create User</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
